import { useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import * as CONSTANTS from '../shared/constants';

const { ANIMATION_BASE_INTERVAL, DEFAULT_SPEED, GRID_SIZE, BOMB_KICK_SPEED, CANVAS_WIDTH, CANVAS_HEIGHT } = CONSTANTS;


const useSocketConnection = (
  roomId,
  gameStateHandlers,
  storageHandlers,
  audioHandlers,
  nickname
) => {
  const socketRef = useRef(null);
  const latencyRef = useRef(0);
  const lastPingRef = useRef(0);
  
  const {
    setGameState,
    setConnectionError,
    setSuddenDeath,
    setMyPlayerId,
    setPlayers,
    setObservers,
    setBombs,
    setExplosions,
    setWalls,
    setUpgrades,
    setIsObserver,
    setWinner,
    setGameOverCountdown,
    setLobbyCountdown,
    setGameTimer,
    setPlayerSpawnCorners,
    setSelectedMap,
    setAvailableMaps,
    myPlayerIdRef,
    myUniquePlayerIdRef,
    playerStateBuffers,
    bombStateBuffers,
    playerAnimationState,
    initializeRef
  } = gameStateHandlers;
  
  const {
    getStoredPlayerId,
    setStoredPlayerId,
    getStoredUniquePlayerId,
    setStoredUniquePlayerId
  } = storageHandlers;
  
  const {
    playRandomBombSound,
    playRandomBombDropSound,
    playRandomBombKickSound,
    playRandomBombStopSound,
    playRandomBombBounceSound,
    playRandomUpgradePickupSound,
    playSuddenDeathStartSound,
    playRandomSuddenDeathBlockSound,
    handleSoundtrackUpdate,
    stopSoundtrack,
    playPoopSound
  } = audioHandlers;

  useEffect(() => {
    // Prevent double initialization in React StrictMode
    if (initializeRef.current) {
      return;
    }
    initializeRef.current = true;
    
    const serverUrl = process.env.NODE_ENV === 'production' 
      ? process.env.REACT_APP_SERVER_URL || 'https://bomberman-server-1080317190266.europe-west1.run.app'
      : 'http://localhost:8080';
    
    // Configure Socket.IO with enhanced connection stability
    socketRef.current = io(serverUrl, {
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 500,
      reconnectionDelayMax: 3000,
      autoConnect: true,
      transports: ['websocket', 'polling'],
      timeout: 60000, // Increased from 30s to 60s
      forceNew: false,
      upgrade: true,
      rememberUpgrade: true,
      // Heartbeat settings to prevent idle disconnections
      pingInterval: 25000, // Send ping every 25 seconds (default is 25000)
      pingTimeout: 20000,  // Wait 20 seconds for pong (default is 20000)
    });
    
    const socket = socketRef.current;

    // Set up periodic ping monitoring to keep connection alive
    // This is separate from Socket.IO's built-in ping and helps detect issues faster
    const pingInterval = setInterval(() => {
      if (socket.connected) {
        lastPingRef.current = Date.now();
        socket.emit('ping');
        
      }
    }, 15000); // Ping every 15 seconds (more reasonable interval)

    // Connection event handlers
    socket.on('connect', () => {
      
      // Try to restore player ID from storage if lost
      if (!myPlayerIdRef.current) {
        const storedPlayerId = getStoredPlayerId();
        if (storedPlayerId) {
          myPlayerIdRef.current = storedPlayerId;
          setMyPlayerId(storedPlayerId);
        }
      }
      if (!myUniquePlayerIdRef.current) {
        const storedUniquePlayerId = getStoredUniquePlayerId();
        if (storedUniquePlayerId) {
          myUniquePlayerIdRef.current = storedUniquePlayerId;
        } else {
        }
      }
      
      // Check if we're in a game room (from URL or prop)
      const currentRoomId = roomId || (window.location.pathname.includes('/game/') ? window.location.pathname.split('/').pop() : null);
      
      if (currentRoomId) {
        // If we have a unique player ID, try to rejoin as existing player
        if (myUniquePlayerIdRef.current) {
          socket.emit('rejoin-room', {
            roomId: currentRoomId,
            uniquePlayerId: myUniquePlayerIdRef.current,
            nickname: nickname
          });
        } else {
          socket.emit('join-room', currentRoomId, nickname);
        }
      } else {
        socket.emit('create-room', nickname);
      }
      
      // Request available maps when connecting
      socket.emit('get-available-maps');
    });

    socket.on('connect_error', (error) => {
      
      // Provide user-friendly error messages
      if (error.message.includes('timeout')) {
        setConnectionError('Connection timeout - server may be slow or unreachable');
      } else if (error.message.includes('xhr poll error')) {
        setConnectionError('Network error - check your internet connection');
      } else {
        setConnectionError(`Connection error: ${error.message}`);
      }
    });

    socket.on('disconnect', (reason) => {
      
      // Only show error for non-recoverable disconnects
      if (reason === 'io server disconnect' || reason === 'io client disconnect') {
        setConnectionError('Connection lost. Please refresh the page.');
        setGameState('error');
      } else {
        // For network issues, show reconnecting status
        setConnectionError('Connection lost. Attempting to reconnect...');
        setGameState('reconnecting');
      }
    });
    
    socket.on('reconnect', (attemptNumber) => {
      setConnectionError(null);
      // Room rejoining is handled in the 'connect' event above
    });
    
    socket.on('reconnect_attempt', (attemptNumber) => {
      setConnectionError(`Reconnecting... (Attempt ${attemptNumber}/10)`);
    });
    
    socket.on('reconnect_failed', () => {
      setConnectionError('Failed to reconnect. Please refresh the page.');
      setGameState('error');
    });

    // Ping/Pong for latency monitoring
    socket.on('pong', () => {
      const latency = Date.now() - lastPingRef.current;
      latencyRef.current = latency;
      
    });


    // Room event handlers
    socket.on('room-created', (data) => {
      setMyPlayerId(data.playerId);
      myPlayerIdRef.current = data.playerId;
      setStoredPlayerId(data.playerId);
      if (data.uniquePlayerId) {
        myUniquePlayerIdRef.current = data.uniquePlayerId;
        setStoredUniquePlayerId(data.uniquePlayerId, data.roomId);
      }
      
      const playersData = data.gameState.players || [];
      const playersArray = Array.isArray(playersData) ? playersData : Object.values(playersData);
      setPlayers(new Map(playersArray.map(p => [p.playerId, p])));
      
      setBombs(data.gameState.bombs || []);
      setExplosions(data.gameState.explosions || []);
      setWalls(data.gameState.walls || []);
      setUpgrades(data.gameState.upgrades || []);
      setGameState(data.gameState.gameState || 'lobby');
      if (data.gameState.selectedMap) {
        setSelectedMap(data.gameState.selectedMap);
      }
      
      window.history.pushState({}, '', `/game/${data.roomId}`);
    });

    socket.on('room-joined', (data) => {
      setMyPlayerId(data.playerId);
      myPlayerIdRef.current = data.playerId;
      setStoredPlayerId(data.playerId);
      if (data.uniquePlayerId) {
        myUniquePlayerIdRef.current = data.uniquePlayerId;
        setStoredUniquePlayerId(data.uniquePlayerId, data.roomId);
      }
      setIsObserver(false);
      
      const playersData = data.gameState.players || [];
      const playersArray = Array.isArray(playersData) ? playersData : Object.values(playersData);
      setPlayers(new Map(playersArray.map(p => [p.playerId, p])));
      
      // Initialize animation states for all players
      playersArray.forEach(player => {
        if (!playerAnimationState.current.has(player.playerId)) {
          playerAnimationState.current.set(player.playerId, {
            direction: 'front',
            frame: 0,
            lastMoveTime: 0,
            lastFrameChangeTime: 0
          });
        }
      });
      
      // Restore player spawn corners on join (in case of mid-game join)
      if (data.gameState.gameState === 'playing') {
        const spawnCorners = new Map();
        const corners = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];
        playersArray.forEach((player, index) => {
          const corner = corners[player.playerId] || corners[index];
          spawnCorners.set(player.playerId, corner);
        });
        setPlayerSpawnCorners(spawnCorners);
      }
      
      setObservers(data.gameState.observers || []);
      setBombs(data.gameState.bombs || []);
      setExplosions(data.gameState.explosions || []);
      setWalls(data.gameState.walls || []);
      setUpgrades(data.gameState.upgrades || []);
      setGameState(data.gameState.gameState || 'lobby');
      if (data.gameState.selectedMap) {
        setSelectedMap(data.gameState.selectedMap);
      }
    });

    socket.on('room-rejoined', (data) => {
      setMyPlayerId(data.playerId);
      myPlayerIdRef.current = data.playerId;
      setStoredPlayerId(data.playerId);
      if (data.uniquePlayerId) {
        myUniquePlayerIdRef.current = data.uniquePlayerId;
        setStoredUniquePlayerId(data.uniquePlayerId, data.roomId);
      }
      setIsObserver(false);
      
      const playersData = data.gameState.players || [];
      const playersArray = Array.isArray(playersData) ? playersData : Object.values(playersData);
      setPlayers(new Map(playersArray.map(p => [p.playerId, p])));
      
      // Initialize animation states for all players on reconnection
      playersArray.forEach(player => {
        if (!playerAnimationState.current.has(player.playerId)) {
          playerAnimationState.current.set(player.playerId, {
            direction: 'front',
            frame: 0,
            lastMoveTime: 0,
            lastFrameChangeTime: 0
          });
        }
      });
      
      // Restore player spawn corners on reconnection
      const spawnCorners = new Map();
      const corners = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];
      playersArray.forEach((player, index) => {
        const corner = corners[player.playerId] || corners[index];
        spawnCorners.set(player.playerId, corner);
      });
      setPlayerSpawnCorners(spawnCorners);
      
      setObservers(data.gameState.observers || []);
      setBombs(data.gameState.bombs || []);
      setExplosions(data.gameState.explosions || []);
      setWalls(data.gameState.walls || []);
      setUpgrades(data.gameState.upgrades || []);
      setGameState(data.gameState.gameState || 'lobby');
      if (data.gameState.selectedMap) {
        setSelectedMap(data.gameState.selectedMap);
      }
      setConnectionError(null);
    });
    
    socket.on('rejoin-failed', (data) => {
      setConnectionError('Failed to rejoin game. ' + data.error);
      setGameState('error');
    });
    
    socket.on('room-joined-as-observer', (data) => {
      setMyPlayerId(null);
      myPlayerIdRef.current = null;
      myUniquePlayerIdRef.current = null;
      setIsObserver(true);
      
      const playersData = data.gameState.players || [];
      const playersArray = Array.isArray(playersData) ? playersData : Object.values(playersData);
      setPlayers(new Map(playersArray.map(p => [p.playerId, p])));
      
      // Initialize animation states for all players
      playersArray.forEach(player => {
        if (!playerAnimationState.current.has(player.playerId)) {
          playerAnimationState.current.set(player.playerId, {
            direction: 'front',
            frame: 0,
            lastMoveTime: 0,
            lastFrameChangeTime: 0
          });
        }
      });
      
      // Restore player spawn corners for observers
      const spawnCorners = new Map();
      const corners = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];
      playersArray.forEach((player, index) => {
        const corner = corners[player.playerId] || corners[index];
        spawnCorners.set(player.playerId, corner);
      });
      setPlayerSpawnCorners(spawnCorners);
      
      setObservers(data.gameState.observers || []);
      setBombs(data.gameState.bombs || []);
      setExplosions(data.gameState.explosions || []);
      setWalls(data.gameState.walls || []);
      setUpgrades(data.gameState.upgrades || []);
      setGameState(data.gameState.gameState || 'lobby');
    });

    socket.on('joined-as-player', (data) => {
      setMyPlayerId(data.playerId);
      myPlayerIdRef.current = data.playerId;
      setStoredPlayerId(data.playerId);
      setIsObserver(false);
    });

    socket.on('room-error', (error) => {
      setConnectionError(error);
      setGameState('error');
    });

    // Player Events
    socket.on('player-joined', (playerData) => {
      setPlayers(prev => new Map(prev.set(playerData.playerId, playerData)));
    });

    socket.on('player-left', (playerId) => {
      setPlayers(prev => {
        const newPlayers = new Map(prev);
        newPlayers.delete(playerId);
        return newPlayers;
      });
    });
    
    socket.on('player-disconnected', (data) => {
      setPlayers(prev => {
        const newPlayers = new Map(prev);
        const player = newPlayers.get(data.playerId);
        if (player) {
          player.connected = false;
          newPlayers.set(data.playerId, { ...player });
        }
        return newPlayers;
      });
    });
    
    socket.on('player-reconnected', (playerData) => {
      setPlayers(prev => {
        const newPlayers = new Map(prev);
        newPlayers.set(playerData.playerId, playerData);
        return newPlayers;
      });
    });

    // Game State Events
    socket.on('game-state-update', (roomData) => {
      const playersData = roomData.players || [];
      const playersArray = Array.isArray(playersData) ? playersData : Object.values(playersData);
      
      setPlayers(prev => {
        const newPlayers = new Map(playersArray.map(p => [p.playerId, p]));
        
        if (myPlayerIdRef.current && prev.has(myPlayerIdRef.current)) {
          const localPlayer = prev.get(myPlayerIdRef.current);
          const serverPlayer = newPlayers.get(myPlayerIdRef.current);
          if (serverPlayer && localPlayer) {
            newPlayers.set(myPlayerIdRef.current, {
              ...serverPlayer,
              x: localPlayer.x,
              y: localPlayer.y
            });
          }
        }
        
        return newPlayers;
      });
      
      setObservers(roomData.observers || []);
      setBombs(roomData.bombs || []);
      setExplosions(roomData.explosions || []);
      setWalls(roomData.walls || []);
      setGameState(roomData.gameState || 'lobby');
      if (roomData.gameTimer !== undefined) {
        setGameTimer(roomData.gameTimer);
      }
      if (roomData.suddenDeath !== undefined) {
        setSuddenDeath(roomData.suddenDeath);
      }
      if (roomData.selectedMap) {
        setSelectedMap(roomData.selectedMap);
      }
    });

    // Observer Events
    socket.on('observer-joined', (observerData) => {
      setObservers(prev => [...prev, observerData]);
    });

    socket.on('observer-left', (observerData) => {
      setObservers(prev => prev.filter(obs => obs.id !== observerData.id));
    });

    socket.on('observer-became-player', (data) => {
      setObservers(prev => prev.filter(obs => obs.id !== data.observerData.id));
      setPlayers(prev => new Map(prev.set(data.playerData.playerId, data.playerData)));
    });

    socket.on('became-observer', (data) => {
      setMyPlayerId(null);
      myPlayerIdRef.current = null;
      myUniquePlayerIdRef.current = null;
      setIsObserver(true);
    });

    socket.on('player-became-observer', (data) => {
      setPlayers(prev => {
        const newPlayers = new Map(prev);
        newPlayers.delete(data.playerData.playerId);
        return newPlayers;
      });
      setObservers(prev => [...prev, data.observerData]);
      setLobbyCountdown(0);
    });

    // Player Movement Events
    socket.on('player-moved', (data) => {
      if (data.playerId === myPlayerIdRef.current) return;
      
      const now = performance.now();
      let buffer = playerStateBuffers.current.get(data.playerId);
      if (!buffer) {
        buffer = [];
        playerStateBuffers.current.set(data.playerId, buffer);
      }
      
      let animState = playerAnimationState.current.get(data.playerId);
      if (!animState) {
        animState = { direction: 'front', frame: 0, lastMoveTime: 0, lastFrameChangeTime: 0 };
        playerAnimationState.current.set(data.playerId, animState);
      }
      
      if (buffer.length > 0) {
        const lastPos = buffer[buffer.length - 1];
        const dx = data.x - lastPos.x;
        const dy = data.y - lastPos.y;
        
        if (Math.abs(dx) > Math.abs(dy)) {
          animState.direction = dx > 0 ? 'right' : 'left';
        } else if (Math.abs(dy) > 0.1) {
          animState.direction = dy > 0 ? 'front' : 'back';
        }
        
        const isMoving = Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5;
        
        if (isMoving) {
          const movementDistance = Math.sqrt(dx * dx + dy * dy);
          const estimatedSpeed = Math.max(movementDistance, DEFAULT_SPEED);
          const baseAnimSpeed = ANIMATION_BASE_INTERVAL;
          const speedMultiplier = DEFAULT_SPEED / estimatedSpeed;
          const animInterval = baseAnimSpeed * speedMultiplier * 0.5;
          
          if (Date.now() - animState.lastMoveTime > animInterval) {
            animState.frame = (animState.frame % 3) + 1;
            animState.lastMoveTime = Date.now();
            animState.lastFrameChangeTime = Date.now();
          }
        } else {
          if (animState.frame !== 0) {
            animState.frame = 0;
            animState.lastFrameChangeTime = Date.now();
          }
        }
      }
      
      buffer.push({
        time: now,
        x: data.x,
        y: data.y
      });
      
      while (buffer.length > 60) {
        buffer.shift();
      }
    });


    // Bomb Events
    socket.on('bomb-placed', (bomb) => {
      setBombs(prev => [...prev, bomb]);
      playRandomBombDropSound();
    });

    socket.on('bomb-kicked', (data) => {
      setBombs(prev => prev.map(bomb => {
        if (bomb.id === data.bombId) {
          const now = performance.now();
          const initialPixelX = bomb.x * GRID_SIZE;
          const initialPixelY = bomb.y * GRID_SIZE;
          
          let buffer = bombStateBuffers.current.get(data.bombId);
          if (!buffer) {
            buffer = [];
            bombStateBuffers.current.set(data.bombId, buffer);
          }
          
          buffer.push({
            time: now,
            pixelX: initialPixelX,
            pixelY: initialPixelY
          });

          return {
            ...bomb,
            moving: true,
            velocityX: data.direction.x * BOMB_KICK_SPEED,
            velocityY: data.direction.y * BOMB_KICK_SPEED,
            pixelX: initialPixelX,
            pixelY: initialPixelY
          };
        }
        return bomb;
      }));
      playRandomBombKickSound();
    });

    socket.on('bomb-exploded', (data) => {
      bombStateBuffers.current.delete(data.bombId);
      setBombs(prev => prev.filter(b => b.id !== data.bombId));
      setExplosions(prev => [...prev, ...data.explosions]);
      playRandomBombSound();
    });

    socket.on('explosions-update', (explosions) => {
      setExplosions(explosions);
    });

    socket.on('bomb-update', (data) => {
      setBombs(prev => prev.map(bomb => {
        if (bomb.id === data.bombId) {
          return { ...bomb, safe: data.safe };
        }
        return bomb;
      }));
    });

    socket.on('bomb-moved', (data) => {
      const now = performance.now();
      let buffer = bombStateBuffers.current.get(data.bombId);
      if (!buffer) {
        buffer = [];
        bombStateBuffers.current.set(data.bombId, buffer);
      }
      
      buffer.push({
        time: now,
        pixelX: data.pixelX,
        pixelY: data.pixelY
      });
      
      while (buffer.length > 60) {
        buffer.shift();
      }

      setBombs(prev => prev.map(bomb => {
        if (bomb.id === data.bombId) {
          return {
            ...bomb,
            x: data.x,
            y: data.y,
            pixelX: data.pixelX,
            pixelY: data.pixelY
          };
        }
        return bomb;
      }));
    });

    socket.on('bomb-stopped', (data) => {
      bombStateBuffers.current.delete(data.bombId);
      
      setBombs(prev => prev.map(bomb => {
        if (bomb.id === data.bombId) {
          return {
            ...bomb,
            x: data.x,
            y: data.y,
            moving: data.moving,
            pixelX: data.x * GRID_SIZE,
            pixelY: data.y * GRID_SIZE
          };
        }
        return bomb;
      }));
      playRandomBombStopSound();
    });

    socket.on('bomb-bounced', (data) => {
      setBombs(prev => prev.map(bomb => {
        if (bomb.id === data.bombId) {
          // Update bomb state after bounce
          return {
            ...bomb,
            x: data.x,
            y: data.y,
            pixelX: data.pixelX,
            pixelY: data.pixelY,
            direction: data.direction,
            moving: true
          };
        }
        return bomb;
      }));
      playRandomBombBounceSound();
    });

    // Wall Events
    socket.on('walls-melting', (meltingWalls) => {
      setWalls(prev => prev.map(wall => {
        const isMelting = meltingWalls.some(m => m.x === wall.x && m.y === wall.y);
        if (isMelting) {
          return { ...wall, melting: true };
        }
        return wall;
      }));
    });

    socket.on('walls-destroyed', (destroyedWalls) => {
      setWalls(prev => prev.filter(wall => 
        !destroyedWalls.some(destroyed => destroyed.x === wall.x && destroyed.y === wall.y)
      ));
    });

    // Upgrade Events
    socket.on('upgrades-spawned', (newUpgrades) => {
      setUpgrades(prev => [...prev, ...newUpgrades]);
    });

    socket.on('upgrades-picked-up', (pickedUpUpgrades) => {
      pickedUpUpgrades.forEach(pickup => {
        playRandomUpgradePickupSound();
      });
      
      setUpgrades(prev => prev.filter(upgrade => 
        !pickedUpUpgrades.some(pickup => pickup.upgradeId === upgrade.id)
      ));
    });

    socket.on('upgrades-melting', (meltingUpgrades) => {
      setUpgrades(prev => prev.map(upgrade => {
        const isMelting = meltingUpgrades.some(m => m.id === upgrade.id);
        if (isMelting) {
          return { ...upgrade, melting: true };
        }
        return upgrade;
      }));
    });

    socket.on('upgrades-destroyed', (destroyedUpgrades) => {
      setUpgrades(prev => prev.filter(upgrade => 
        !destroyedUpgrades.some(destroyed => destroyed.id === upgrade.id)
      ));
    });

    socket.on('upgrades-update', (allUpgrades) => {
      setUpgrades(allUpgrades);
    });

    // Player Death Event
    socket.on('player-died', (playerId) => {
      setPlayers(prev => {
        const newPlayers = new Map(prev);
        const player = newPlayers.get(playerId);
        if (player) {
          player.alive = false;
        }
        return newPlayers;
      });
    });

    // Server-authoritative game updates
    socket.on('game-tick', (data) => {
      // Update all player positions from server
      setPlayers(prev => {
        const newPlayers = new Map(prev);
        data.players.forEach(serverPlayer => {
          const existingPlayer = newPlayers.get(serverPlayer.playerId);
          if (existingPlayer) {
            existingPlayer.x = serverPlayer.x;
            existingPlayer.y = serverPlayer.y;
            existingPlayer.alive = serverPlayer.alive;
            existingPlayer.direction = serverPlayer.direction;
            existingPlayer.frame = serverPlayer.frame;
            existingPlayer.diseases = serverPlayer.diseases || {};
          }
        });
        return newPlayers;
      });
      
      // Update bomb positions from server, but preserve interpolation buffers for moving bombs
      setBombs(prev => {
        // Create a map of existing bombs for quick lookup
        const existingBombsMap = new Map(prev.map(b => [b.id, b]));
        
        return data.bombs.map(serverBomb => {
          const existingBomb = existingBombsMap.get(serverBomb.id);
          
          // If bomb is moving, update buffer for interpolation
          if (serverBomb.moving && serverBomb.pixelX !== undefined && serverBomb.pixelY !== undefined) {
            let buffer = bombStateBuffers.current.get(serverBomb.id);
            if (!buffer) {
              buffer = [];
              bombStateBuffers.current.set(serverBomb.id, buffer);
            }
            
            const now = performance.now();
            buffer.push({
              time: now,
              pixelX: serverBomb.pixelX,
              pixelY: serverBomb.pixelY
            });
            
            // Keep buffer size reasonable
            while (buffer.length > 10) {
              buffer.shift();
            }
          }
          
          return {
            ...serverBomb,
            timer: serverBomb.timer || 3000,
            // Preserve client-side velocity for smooth animation
            velocityX: existingBomb?.velocityX,
            velocityY: existingBomb?.velocityY,
            // Preserve client-side gelatinous property
            gelatinous: existingBomb?.gelatinous
          };
        });
      });
      
      // Update game timer
      if (data.gameTimer !== undefined) {
        setGameTimer(data.gameTimer);
      }
      
      // Handle soundtrack updates
      if (data.soundtrack && data.soundtrackStartTime) {
        handleSoundtrackUpdate(data.soundtrack, data.soundtrackStartTime);
      }
    });

    // Debug Event Handler - logs server debug messages to browser console
    socket.on('debug-log', (data) => {
      // Debug messages disabled for production
      // console.log(`🔧 [Server Debug] ${data.message}`, data.data || '');
    });

    // Sudden Death Events
    socket.on('sudden-death-start', () => {
      setSuddenDeath(true);
      playSuddenDeathStartSound();
      // console.log('⚠️ SUDDEN DEATH MODE ACTIVATED!');
    });

    socket.on('sudden-death-block', (data) => {
      // Update walls with the new sudden death block
      setWalls(prev => [...prev, {
        x: data.x,
        y: data.y,
        destructible: false,
        suddenDeath: true
      }]);
      
      // Play sound effect for block spawn
      playRandomSuddenDeathBlockSound();
      
      // Handle any players that were killed
      if (data.killedPlayers && data.killedPlayers.length > 0) {
        setPlayers(prev => {
          const newPlayers = new Map(prev);
          data.killedPlayers.forEach(killedPlayer => {
            const player = newPlayers.get(killedPlayer.playerId);
            if (player) {
              player.alive = false;
            }
          });
          return newPlayers;
        });
      }
      
      // console.log(`💀 Sudden Death block spawned at (${data.x}, ${data.y})`);
    });

    // Map Events
    socket.on('available-maps', (maps) => {
      setAvailableMaps(maps);
    });

    socket.on('map-changed', (data) => {
      setSelectedMap(data.mapId);
      setWalls(data.walls);
      // console.log(`🗺️ Map changed to: ${data.mapName}`);
    });

    // Game Flow Events
    socket.on('game-over', (data) => {
      setWinner(data.winner);
      setGameState('gameOver');
      setGameOverCountdown(Math.ceil(data.countdown / 1000));
      
      // Stop soundtrack when game ends
      stopSoundtrack();
      
      setTimeout(() => {
        if (window.getRenderStats) {
          window.getRenderStats();
        }
      }, 1000);
    });

    socket.on('game-over-countdown', (data) => {
      setGameOverCountdown(data.countdown);
    });

    socket.on('game-reset', (data) => {
      // Stop any playing soundtrack when game resets to lobby
      stopSoundtrack();
      
      playerStateBuffers.current.clear();
      bombStateBuffers.current.clear();
      
      setPlayers(new Map(data.gameState.players.map(p => [p.playerId, p])));
      setBombs(data.gameState.bombs);
      setExplosions(data.gameState.explosions);
      setWalls(data.gameState.walls);
      setUpgrades(data.gameState.upgrades || []);
      setGameState(data.gameState.gameState);
      setWinner(null);
      setGameOverCountdown(0);
      setLobbyCountdown(0);
      setSuddenDeath(data.gameState.suddenDeath || false);
    });

    socket.on('player-ready-changed', (data) => {
      setPlayers(prev => {
        const newPlayers = new Map(prev);
        for (const [, player] of newPlayers) {
          if (player.playerId === data.playerId) {
            player.ready = data.ready;
            break;
          }
        }
        return newPlayers;
      });
    });

    socket.on('lobby-countdown-start', (data) => {
      setLobbyCountdown(Math.ceil(data.countdown / 1000));
    });

    socket.on('lobby-countdown-update', (data) => {
      setLobbyCountdown(data.countdown);
    });

    socket.on('lobby-countdown-cancel', () => {
      setLobbyCountdown(0);
    });

    socket.on('play-sound', (data) => {
      if (data.sound) {
        playPoopSound(data.sound);
      }
    });

    socket.on('game-start', (data) => {
      // Stop any playing soundtrack before starting new game
      stopSoundtrack();
      
      setPlayers(new Map(data.gameState.players.map(p => [p.playerId, p])));
      setBombs(data.gameState.bombs);
      setExplosions(data.gameState.explosions);
      setWalls(data.gameState.walls);
      setUpgrades([]);
      setGameState(data.gameState.gameState);
      setLobbyCountdown(0);
      setSuddenDeath(false);
      
      const spawnCorners = new Map();
      data.gameState.players.forEach(player => {
        const playerX = player.x;
        const playerY = player.y;
        
        const isLeft = playerX < CANVAS_WIDTH / 2;
        const isTop = playerY < CANVAS_HEIGHT / 2;
        
        let corner = '';
        if (isTop && isLeft) corner = 'top-left';
        else if (isTop && !isLeft) corner = 'top-right';
        else if (!isTop && isLeft) corner = 'bottom-left';
        else corner = 'bottom-right';
        
        spawnCorners.set(player.playerId, corner);
      });
      setPlayerSpawnCorners(spawnCorners);
      
      playerStateBuffers.current.clear();
      bombStateBuffers.current.clear();
      
      if (window.resetRenderStats) {
        window.resetRenderStats();
      }
    });


    return () => {
      clearInterval(pingInterval);
      if (socket.connected) {
        socket.removeAllListeners();
        socket.disconnect();
      }
    };
  }, [
    roomId,
    setGameState,
    setConnectionError,
    setMyPlayerId,
    setPlayers,
    setObservers,
    setBombs,
    setExplosions,
    setWalls,
    setUpgrades,
    setIsObserver,
    setWinner,
    setGameOverCountdown,
    setLobbyCountdown,
    setPlayerSpawnCorners,
    myPlayerIdRef,
    myUniquePlayerIdRef,
    playerStateBuffers,
    bombStateBuffers,
    playerAnimationState,
    getStoredPlayerId,
    setStoredPlayerId,
    getStoredUniquePlayerId,
    playRandomBombSound,
    playRandomBombDropSound,
    playRandomBombKickSound,
    playRandomBombStopSound,
    playRandomBombBounceSound,
    playRandomUpgradePickupSound,
    playPoopSound,
    setStoredUniquePlayerId,
    setSelectedMap,
    setAvailableMaps,
    initializeRef
  ]);

  // Emit functions for external use
  const emitPlayerInput = useCallback((data) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('player-input', data);
    }
  }, []);

  const emitKickBomb = useCallback((data) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('kick-bomb', data);
    }
  }, []);

  const emitPlaceBomb = useCallback((data) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('place-bomb', data);
    }
  }, []);

  const emitToggleReady = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.emit('toggle-ready');
    }
  }, []);

  const emitJoinAsPlayer = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.emit('join-as-player', nickname);
    }
  }, [nickname]);

  const emitBecomeObserver = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.emit('become-observer');
    }
  }, []);

  const emitGetAvailableMaps = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.emit('get-available-maps');
    }
  }, []);

  const emitChangeMap = useCallback((mapId) => {
    if (socketRef.current) {
      socketRef.current.emit('change-map', { mapId });
    }
  }, []);

  return {
    socketRef,
    emitPlayerInput,
    emitKickBomb,
    emitPlaceBomb,
    emitToggleReady,
    emitJoinAsPlayer,
    emitBecomeObserver,
    emitGetAvailableMaps,
    emitChangeMap
  };
};

export default useSocketConnection;